import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Percent, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Slim promotional strip: newsletter subscription with a 5% discount hook.
 * Sits under the hero on Home and city landings.
 */
export default function NewsletterPromoBar({ source = "hero-bar" }) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) return toast.error("Introduza um email válido");
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", { email, source, rgpd_consent: true });
            if (data.already) toast.info("Já se encontra subscrito. Obrigado!");
            else toast.success("Subscrição feita — verifique o seu email.");
            setDone(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erro ao subscrever. Tente novamente.");
        } finally { setLoading(false); }
    };

    return (
        <section className="py-6 sm:py-8" data-testid={`newsletter-bar-${source}`}>
            <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                <div className="bg-[hsl(var(--copper))] text-white rounded-[2rem] px-6 py-6 sm:px-10 sm:py-6 flex flex-col lg:flex-row lg:items-center gap-5">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-white/15 border border-white/30 flex items-center justify-center flex-shrink-0">
                            <Percent className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[11px] uppercase tracking-widest font-mono text-white/80">Newsletter · Bem feito. Ponto.</div>
                            <p className="font-display font-bold text-lg sm:text-xl leading-tight mt-0.5">
                                Fique a par das nossas obras e novidades <span className="opacity-90">— </span>
                                <span className="font-serif-italic">5% de desconto</span> na sua primeira obra connosco.
                            </p>
                        </div>
                    </div>

                    {done ? (
                        <div className="flex items-center gap-2 text-sm bg-white/15 px-4 py-3 rounded-full">
                            <span className="w-7 h-7 rounded-full bg-white text-[hsl(var(--copper))] flex items-center justify-center">
                                <Check className="w-4 h-4" />
                            </span>
                            Obrigado — enviámos-lhe um email de confirmação.
                        </div>
                    ) : (
                        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0" data-testid="newsletter-bar-form">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="o.seu.email@exemplo.pt"
                                className="w-full sm:min-w-[280px] bg-white text-stone-800 placeholder:text-stone-400 rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/60"
                                data-testid="newsletter-bar-email"
                                autoComplete="email"
                            />
                            <button type="submit" disabled={loading}
                                className="bg-[hsl(var(--forest))] hover:bg-[hsl(var(--forest-deep))] text-white rounded-full px-5 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
                                data-testid="newsletter-bar-submit">
                                {loading ? "A enviar…" : "Subscrever & obter 5%"}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </form>
                    )}
                </div>
                <p className="text-[11px] text-stone-500 mt-2 px-2 lg:px-4">
                    Ao subscrever, aceita receber comunicações e a nossa política de privacidade. Sem spam, cancela quando quiser.
                </p>
            </div>
        </section>
    );
}
